import { useRef, useState } from "react";
import { Table, Button } from "react-bootstrap";
import { useToasts } from "react-toast-notifications";
import { useQueryClient } from "react-query";
import { useDispatch } from "react-redux";
import * as XLSX from "xlsx";
import { v4 as uuidv4 } from "uuid";
import Swal from "sweetalert2";

import LoadInventoryToolbar from "./LoadInventoryToolbar";

import { useInitialDataImplementation } from "@/hooks/implementation/firstLoad/initialDataImplementation";
import { useParentEstateBulkLoader } from "@/hooks/implementation/firstLoad/bulkLoader";
import { FETCHING_ADD_DOWNLOAD } from "@/redux/types/main.types";
import { toast } from "@/services/Utils";

import "./rowsLoadStyle.scss";
import FooterButtons from "../Buttons/FooterButtons";

const key = "initial_data_implementation";

const EstateParentsTableLoad = (props) => {
	const { addToast } = useToasts();
	const dispatch = useDispatch();
	const queryClient = useQueryClient();

	const { mutate, isLoading } = useParentEstateBulkLoader();
	const { data } = useInitialDataImplementation();

	const [isLockedDownload, setIsLockedDownload] = useState(false);

	const fileInputRef = useRef(null);

	const addRow = () => {
		const newEstateParent = [...data.estateParents, { hash: uuidv4(), name: "", isNew: true }];
		queryClient.setQueryData([key], { ...data, estateParents: newEstateParent });
	};

	const handleInputChange = (index, field, value) => {
		const newEstateParents = data.estateParents.map((row, i) => (i === index ? { ...row, [field]: value } : row));
		queryClient.setQueryData([key], { ...data, estateParents: newEstateParents });
	};

	const removeRow = (index, isNew) => {
		if (!isNew) {
			Swal.fire({
				title: "Accion de borrado",
				html: "Esta accion tendra efecto al presionar <strong>GUARDAR</strong> o <strong>SIGUIENTE</strong>, ¿deseas proceder?",
				icon: "warning",
				showCancelButton: true,
				confirmButtonColor: "#7fb02f",
				cancelButtonColor: "#d33",
				confirmButtonText: "Sí, eliminarlo!",
				cancelButtonText: "Cancelar",
				didOpen: () => {
					document.body.classList.remove("swal2-height-auto");
				},
			}).then((result) => {
				if (result.isConfirmed) {
					const newEstateParents = data.estateParents.filter((_, i) => i !== index);
					queryClient.setQueryData([key], { ...data, estateParents: newEstateParents });

					Swal.fire({
						title: "Eliminado",
						html: "El campo ha sido eliminado",
						icon: "success",
						confirmButtonColor: "#7fb02f",
						confirmButtonText: "OK",
						didOpen: () => {
							document.body.classList.remove("swal2-height-auto");
						},
					});
				} else {
					toast(addToast, "Accion cancelada por el usuario");
				}
			});
		} else {
			const newEstateParents = data.estateParents.filter((_, i) => i !== index);
			queryClient.setQueryData([key], { ...data, estateParents: newEstateParents });
			toast(addToast, "Campo eliminado");
		}
	};

	const handleExportToExcel = () => {
		setIsLockedDownload(true);
		const filename = "PlanillaCargaCampos.xlsx";
		const params = {};

		const payload = {
			file: {
				key: Array.from({ length: 25 }, () => Math.random().toString(36)[2] || "0").join(""),
				params: params,
				show: true,
				url: "/client/implementation/excel_estates_parents",
				name: filename,
				type: "excel",
				inExecution: false,
				inProgress: false,
				inChecked: false,
			},
		};

		new Promise((resolve, reject) => {
			try {
				dispatch({
					type: FETCHING_ADD_DOWNLOAD,
					payload: payload,
				});

				setTimeout(() => {
					resolve();
				}, 2000);
			} catch (error) {
				reject(error);
			}
		})
			.then(() => {
				setIsLockedDownload(false);
			})
			.catch(() => {
				setIsLockedDownload(false);
			});
	};

	const handleFileUpload = (e) => {
		const file = e.target.files[0];
		if (!file) return;

		const reader = new FileReader();

		reader.onload = (e) => {
			const binaryStr = e.target.result;
			const workbook = XLSX.read(binaryStr, { type: "binary" });
			const firstSheetName = workbook.SheetNames[0];
			const worksheet = workbook.Sheets[firstSheetName];
			const jsonData = XLSX.utils.sheet_to_json(worksheet);

			const updatedEstateParents = [...data.estateParents];

			const newRows = jsonData.map((item) => {
				const hash = item["CODIGO"] || uuidv4();
				const name = item["NOMBRE"] || "";

				const isNew = !updatedEstateParents.some((estateParent) => estateParent.hash === hash);

				return { hash, name, isNew };
			});

			newRows.forEach((newRow) => {
				const existingIndex = updatedEstateParents.findIndex((estateParent) => estateParent.hash === newRow.hash);
				if (existingIndex !== -1) {
					updatedEstateParents[existingIndex] = newRow;
				} else {
					updatedEstateParents.push(newRow);
				}
			});

			queryClient.setQueryData([key], { ...data, estateParents: updatedEstateParents });
		};

		reader.readAsBinaryString(file);

		if (fileInputRef.current) {
			fileInputRef.current.value = "";
		}
	};

	const validEstateParent = (_e) => {
		let isValidBusiness = true;

		for (let i = 0; i < data?.estateParents.length; i++) {
			const estateParent = data.estateParents[i];

			if (!estateParent.name) {
				isValidBusiness = false;
				toast(addToast, "El nombre del campo es requerido. Por favor, revisa el elemento " + (i + 1) + " y corrige el nombre.", "error");
				break;
			}
		}

		return isValidBusiness;
	};

	const saveEstateParent = ({ moveForward = false }) => {
		if (validEstateParent()) {
			const queryData = { ...queryClient.getQueryData([key]) };
			queryData.implementationStep = moveForward ? data.implementationStep + 1 : data.implementationStep;

			mutate(queryData, {
				onSuccess: (response) => {
					if (Object.prototype.hasOwnProperty.call(response, "estateParents")) {
						queryClient.setQueryData([key], {
							...response,
							estateParents: response.estateParents,
							implementationStep: response.implementationStep,
							users: response.users,
						});

						if (props.formWizardRef.current?.nextTab && moveForward) {
							props.formWizardRef.current.nextTab();
						}
					}
				},
			});
		}
	};

	if (data.estateParents.length === 0) {
		addRow();
	}

	return (
		<>
			<LoadInventoryToolbar
				handleExportToExcel={handleExportToExcel}
				handleFileUpload={handleFileUpload}
				isLockedDownload={isLockedDownload}
				fileInputRef={fileInputRef}
			/>
			<Table hover>
				<thead>
					<tr>
						<th className="border-0" style={{ width: "96%" }}>
							Campo <small className="text-danger">*</small>
						</th>
						<th className="border-0" style={{ width: "4%" }}></th>
					</tr>
				</thead>
				<tbody>
					{data.estateParents.map((row, index) => (
						<tr key={row.hash}>
							<td className="position-relative td-collapse">
								<input
									type="text"
									className="form-control"
									placeholder="Ingrese el nombre del campo"
									required
									value={row.name}
									onChange={(e) => handleInputChange(index, "name", e.target.value)}
								/>
								{row.isNew && (
									<div className="diagonal-badge">
										<span className="badge badge-info">Nuevo</span>
									</div>
								)}
							</td>
							<td className="td-collapse">
								<Button variant="danger" onClick={() => removeRow(index, row.isNew)}>
									<i className="ti-trash"></i>
								</Button>
							</td>
						</tr>
					))}
					<tr>
						<td className="border-0">
							<button
								className="btn bg-agro-outline-color btn-block tr-btn"
								onClick={addRow}
								style={{ height: "30px", fontSize: "12px", width: "200px" }}
								disabled={isLoading}
							>
								<i className="ti-plus"></i> Agregar
							</button>
						</td>
					</tr>
				</tbody>
			</Table>

			<FooterButtons wizard={props.formWizardRef} currentIndex={props.index} handleSave={saveEstateParent} isLoading={isLoading} />
		</>
	);
};

export default EstateParentsTableLoad;
